import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { CircularProgress } from '@mui/material';
import TranslationIcon from '@/public/icons/approval/translation.svg';
import { Language } from '@/types/api';
import type { AppLanguageCode } from '@/utils/language';
import useRecordTranslationSource from '@/components/Record/TalkFLow/hooks/useRecordTranslationSource';
import {
  recordShowOriginalDocIdsAtom,
  recordTranslationEntriesAtom,
  recordTranslationToLangAtom,
} from '@/components/Record/TalkFLow/translationAtoms';
import useRecordTranslationView from '@/components/Record/TalkFLow/hooks/useRecordTranslationView';

/**
 * 번역 상태 띠 — 색/타이포/아이콘은 전자결재(ApprovalView/TranslationStatusBar)와 동일하게 맞춘다.
 * 포탈 안에서 번역 상태 표시가 화면마다 달라 보이지 않도록 한 벌로 통일하기 위함.
 *
 * 3가지 상태:
 *  1. 번역중        — "AI 번역중" + 스피너 + [취소]
 *  2. 원문 변경됨    — "원문이 변경되었어요" + [다시 번역] + [취소]
 *     회의록은 AI 정리/AI 요약 재생성·자동저장·AI 채팅 반영으로 원문이 수시로 바뀐다.
 *     번역본을 말없이 두면 사용자가 옛 내용을 최신인 줄 알고 읽게 되므로 띠로 알린다.
 *     (번역본 자체는 유지 — 갑자기 한국어로 튀면 외국어 사용자에게 더 혼란스럽다)
 *  3. 번역됨        — "AI 번역됨 (언어)" + [원문 보기 / 번역 보기] + [취소]
 *
 * 취소: 진행 중이면 in-flight 를 중단하고 이 문서 항목의 `isApplied` 만 false 로 내린다.
 * 번역 결과 자체는 남겨둔다 → 같은 언어로 다시 켤 때 재호출이 없다.
 */
type Props = {
  /** 현재 문서 id — 번역 상태를 문서별로 조회하는 키 */
  docId: string;
  /** 진행 중 번역 중단 (useRecordTranslation.cancel) */
  onCancel?: () => void;
  /** 원문 변경 후 재번역 (useRecordTranslation.execute) */
  onRetranslate?: (language: AppLanguageCode) => void;
};

const TranslationStatusBar = ({ docId, onCancel, onRetranslate }: Props) => {
  const { t } = useTranslation();
  const { entry, hasCurrentResult, isTranslating, showOriginal } =
    useRecordTranslationView(docId);
  const setEntries = useSetAtom(recordTranslationEntriesAtom);
  const setShowOriginalDocIds = useSetAtom(recordShowOriginalDocIdsAtom);
  const targetLang = useAtomValue(recordTranslationToLangAtom);
  const { fingerprint, isHydrated } = useRecordTranslationSource(docId);

  // 취소는 적용만 해제한다 — 결과는 남겨 같은 언어로 다시 켤 때 재호출을 피한다.
  const handleCancel = useCallback(() => {
    onCancel?.();
    setEntries((prev) =>
      prev[docId]
        ? { ...prev, [docId]: { ...prev[docId], isApplied: false } }
        : prev,
    );
    setShowOriginalDocIds((prev) => ({ ...prev, [docId]: false }));
  }, [docId, onCancel, setEntries, setShowOriginalDocIds]);

  const languageCode = entry?.targetLanguage ?? targetLang;

  const handleRetranslate = useCallback(() => {
    if (!languageCode) return;
    onRetranslate?.(languageCode);
  }, [languageCode, onRetranslate]);

  if (!isTranslating && !hasCurrentResult) return null;

  const languageDisplay = languageCode ? Language[languageCode] : '';
  // 번역 시점 지문과 현재 지문이 다르면 그 사이에 원문이 바뀐 것.
  // hydration 전 지문은 실제 원문과 다르므로 변경으로 보지 않는다 (오탐 + 반쪽 재번역 방지).
  const isStale =
    !isTranslating &&
    hasCurrentResult &&
    isHydrated &&
    entry?.sourceFingerprint !== undefined &&
    entry.sourceFingerprint !== fingerprint;

  return (
    <Root>
      <Inner>
        <Label>
          <TranslationIcon width={20} height={20} />
          {isTranslating && (
            <>
              {t('AI 번역중')}
              <CircularProgress size={14} color="inherit" />
            </>
          )}
          {!isTranslating && isStale && t('원문이 변경되었어요')}
          {!isTranslating && !isStale && (
            <>
              {t('AI 번역됨')}
              {languageDisplay ? ` (${languageDisplay})` : ''}
            </>
          )}
        </Label>
        <Actions>
          {!isTranslating && isStale && onRetranslate && (
            <TextButton type="button" onClick={handleRetranslate}>
              {t('다시 번역')}
            </TextButton>
          )}
          {!isTranslating && !isStale && (
            <TextButton
              type="button"
              onClick={() =>
                setShowOriginalDocIds((prev) => ({
                  ...prev,
                  [docId]: !showOriginal,
                }))
              }
            >
              {showOriginal ? t('번역보기') : t('원문보기')}
            </TextButton>
          )}
          <TextButton
            type="button"
            aria-label={t('번역 취소')}
            onClick={handleCancel}
          >
            {t('취소')}
          </TextButton>
        </Actions>
      </Inner>
    </Root>
  );
};

export default TranslationStatusBar;

const Root = styled.div`
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 16px;
  border-radius: 8px;
  background-color: var(--dk-purple-soft, #fbf4fa);
  color: rgba(199, 122, 196, 1);
  box-sizing: border-box;
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: rgba(199, 122, 196, 1);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  & > svg {
    flex-shrink: 0;
    color: rgba(199, 122, 196, 1);
  }
`;

const Actions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const TextButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: #c77ac4;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid #c77ac4;
    outline-offset: 2px;
  }
`;
